/**
 * useOrgTopUp.ts — одна дверь кнопки «Пополнить» (WEB-2312, WEB-1895).
 *
 * Зачем. Кнопки «Пополнить» жили по месту, и каждая решала сама, куда вести и
 * кому показываться. Пять из них вели на `/billing` — адрес, которого в роутере
 * кабинета нет (WEB-2312: человек хочет заплатить и попадает в никуда), с
 * перезагрузкой всего приложения. Кнопка редактора вела на правильную
 * страницу, но судила по АКТИВНОЙ организации, а не по организации открытого
 * проекта (WEB-1895): человек в двух организациях видел не ту кнопку и
 * пополнял не тот кошелёк. Теперь все такие кнопки берут и право, и переход
 * здесь.
 *
 * Право (`canTopUp`). Судит организация ОТКРЫТОГО ПРОЕКТА (нет проекта —
 * активная организация, режим организации). Признак — `canManageOrgBilling`,
 * тот же, которым сервер судит денежные пути организации (WEB-1343 C2; так же
 * судит окно генерации, WEB-2383 п.13): рядовому участнику и администратору,
 * которому владелец закрыл деньги, страница «Баланс» ничего не даст — им
 * вместо кнопки пишем «обратитесь к владельцу». Роль неизвестна (список
 * организаций не загрузился) — кнопку показываем: настоящего владельца прятать
 * нельзя (решение ревизии десятки 8, WEB-1895 п.5).
 *
 * Переход (`goToBalance`). Правило пары (WEB-1895 п.4): кнопка, показанная по
 * организации проекта, обязана вести на баланс ТОЙ ЖЕ организации. Страница
 * «Баланс» показывает активную организацию, поэтому, если организация проекта
 * не активна, дверь сперва переключает активную организацию тем же путём, что
 * переключатель организаций (`OrgSwitcher.tsx`): запись на сервер, сброс
 * проекта, проекты и баланс новой организации. Сервер отказал — на баланс не
 * ведём (это был бы чужой кошелёк), показываем отказ. Переход — внутренний
 * (`navigate`), без перезагрузки. Перед сменой проекта спрашиваем экран
 * общей дверью `confirmLeave` (WEB-2910): проект сбрасывается раньше смены
 * адреса. Повторное нажатие, пока идёт первое, ничего не делает — иначе в
 * истории два одинаковых шага и «Назад» приходится жать дважды.
 *
 * Адрес ссылки (`href`, WEB-2952). Вид ссылки (`components/billing/
 * OrgTopUpLink.tsx`) своего знания об адресе «Баланса» не имеет — берёт его
 * здесь, вместе с правом: адрес есть, только когда организация уже активна
 * (иначе новая вкладка по адресу открыла бы чужой кошелёк, возврат приёмки
 * WEB-2312). Других мест с адресом «Баланса» в кабинете нет — это держит
 * сторож `useOrgTopUp.door.web2952.guard.test.ts` (кроме страниц самой
 * активной организации, поимённо и с причиной).
 *
 * Организация явно (`forOrgId`, WEB-2952). Плашка заморозки в режиме
 * организации говорит о проекте, который не открыт, — его организацию она
 * передаёт аргументом. Не задана — организация открытого проекта, иначе
 * активная.
 *
 * Продажа пакетов (`exit`, WEB-3327). Право ещё не значит, что пополнить
 * можно: пока продажа пакетов выключена (`TOPUP_ENABLED`, `VITE_TOPUP_ENABLED`),
 * страница «Баланс» показывает пакеты серыми — кнопка «Пополнить» вела бы в
 * тупик. Выход берётся той же развилкой, что у долга (`resolveDebtExit` в
 * `components/billing/debtExit.ts`): право есть и продажа включена —
 * `topup` (кнопка); право есть, продажа выключена — `support` (фраза
 * `supportNote`, без кнопки и без адреса ссылки); права нет — `owner`
 * («обратитесь к владельцу»). `canTopUp` — это ровно `topup`; само право —
 * `canSettle`: им судят места, где «Баланс» нужен не ради покупки пакетов
 * (плашка долга редактора выбирает фразу, плашка заморозки ведёт разбираться
 * со спорным платежом). Переход `goToBalance` продажу не судит — его
 * показывает или прячет вызывающий по `exit`/`canTopUp`.
 *
 * Хук требует контекста роутера (`useNavigate`): места, где кнопка рисуется не
 * всегда, зовут его из маленького компонента, который монтируется только
 * вместе с кнопкой (как `EditorTopUpButton`, WEB-1246).
 */
import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { useAppDispatch, useAppSelector } from './hooks';
import { useOrgRole } from './useOrgRole';
import { switchActiveOrgThunk } from '@/store/organizationsSlice';
import {
  clearActiveProjectThunk,
  fetchProjectsThunk,
} from '@/store/projectsSlice';
import { fetchBillingThunk } from '@/store/billingSlice';
import { confirmLeave } from '@/utils/leaveGuard';
import { localizeError } from '@/utils/localizeError';
import { pickStrings, useStrings } from '@/i18n';
import { settingsStrings } from '@/i18n/dictionaries/settings';
import { billingStrings } from '@/i18n/dictionaries/billing';
import { TOPUP_ENABLED } from '@/config/topup-enabled';
import {
  resolveDebtExit,
  type DebtExitKind,
} from '@/components/billing/debtExit';

/** Страница «Баланс» организации — маршрут `org/balance` (router/index.tsx). */
export const ORG_BALANCE_PATH = '/org/balance';

export interface OrgTopUp {
  /**
   * Показывать кнопку «Пополнить»: право есть И продажа пакетов включена
   * (`exit === 'topup'`). Иначе — `supportNote` (`exit === 'support'`) или
   * «обратитесь к владельцу» (`exit === 'owner'`).
   */
  canTopUp: boolean;
  /** Выход человека: пополнение, поддержка или владелец (WEB-3327). */
  exit: DebtExitKind;
  /** Право распоряжаться деньгами организации — без учёта продажи пакетов. */
  canSettle: boolean;
  /** Фраза вместо кнопки при выключенной продаже (`exit === 'support'`). */
  supportNote: string;
  /** Организация, чей баланс пополняется: проекта, иначе активная. */
  orgId: string | null;
  /**
   * Организация проекта не активна: «Баланс» по одному адресу покажет ЧУЖОЙ
   * кошелёк (активной организации), пока дверь не переключит. Ссылке с адресом
   * нельзя отдавать браузеру новую вкладку (ctrl/cmd/shift, средняя кнопка) —
   * возврат приёмки WEB-2312.
   */
  needsSwitch: boolean;
  /**
   * Адрес для ссылки: «Баланс», когда организация уже активна; иначе
   * `undefined` — ссылка без адреса ведёт только через дверь (WEB-2952).
   */
  href: string | undefined;
  /** Перейти на баланс этой организации (с переключением, если нужно). */
  goToBalance: () => Promise<void>;
}

export function useOrgTopUp(forOrgId?: string | null): OrgTopUp {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // Слайсы читаются с `?.`: кнопка живёт внутри оболочки редактора, а её
  // наборы собирают частичный стор (без projects/organizations). В бою оба
  // слайса есть всегда; без них дверь ведёт себя как «проекта нет».
  const projectOrgId = useAppSelector(
    (state) => state.projects?.currentProject?.orgId ?? null,
  );
  const activeOrgId = useAppSelector(
    (state) => state.organizations?.activeOrgId ?? null,
  );
  const { role, canManageOrgBilling, orgId } = useOrgRole(
    forOrgId ?? projectOrgId ?? undefined,
  );
  const busy = useRef(false);
  const billing = useStrings(billingStrings);
  const canSettle = role === null || canManageOrgBilling;
  // Признак продажи читается при каждом рисовании, как в debtExit.ts.
  const exit = resolveDebtExit({ canSettle, topupEnabled: TOPUP_ENABLED });

  const goToBalance = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      if (!(await confirmLeave())) return;
      if (orgId && orgId !== activeOrgId) {
        const result = await dispatch(switchActiveOrgThunk(orgId));
        if (!switchActiveOrgThunk.fulfilled.match(result)) {
          void message.error(
            localizeError(
              result.payload,
              pickStrings(settingsStrings).workspaceSwitchFailed,
            ),
          );
          return;
        }
        void dispatch(clearActiveProjectThunk());
        void dispatch(fetchProjectsThunk(orgId));
        void dispatch(fetchBillingThunk(undefined));
      } else {
        // Симметрия WEB-731 (HeaderBalanceWidget): орг-адрес — сброс проекта.
        void dispatch(clearActiveProjectThunk());
      }
      navigate(ORG_BALANCE_PATH);
    } finally {
      busy.current = false;
    }
  }, [orgId, activeOrgId, dispatch, navigate]);

  const needsSwitch = orgId !== null && orgId !== activeOrgId;
  return {
    canTopUp: exit === 'topup',
    exit,
    canSettle,
    supportNote: billing.topUpSupportNote,
    orgId,
    needsSwitch,
    href: needsSwitch || exit === 'support' ? undefined : ORG_BALANCE_PATH,
    goToBalance,
  };
}
